import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import {
  Thermometer,
  Plus,
  Edit,
  Trash2,
  Bell,
  Pause,
  Navigation,
  AlertOctagon,
  Radio,
  Settings,
  Save } from
'lucide-react';

export const ColdChainRules = () => {const { t } = useTranslation("ezparcel");
  const [rules, setRules] = useState([]);

  const actionIcons = {
    'NOTIFY_QA': Bell,
    'NOTIFY_DISPATCHER': Radio,
    'HOLD_SHIPMENT': Pause,
    'REROUTE_COLD_FACILITY': Navigation,
    'EMERGENCY_STOP': AlertOctagon
  };

  const toggleRule = (ruleId) => {
    setRules((prev) => prev.map((rule) =>
    rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };

  return (
    <div className="space-y-6" data-testid="coldchain-rules">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Settings className="h-8 w-8 text-cyan-600" />{t("coldChainRules", "Cold Chain Rules")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureTemperatureThresholds", "Configure temperature thresholds and automated responses")}

          </p>
        </div>
        <Button className="gap-2 bg-cyan-600 hover:bg-cyan-700">
          <Plus className="h-4 w-4" />{t("createRule", "Create Rule")}

        </Button>
      </div>

      {/* Rules Grid */}
      <div className="grid gap-4 lg:grid-cols-2">
        {rules.map((rule) =>
        <Card key={rule.id} className={`card-elevated ${!rule.enabled && 'opacity-60'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Thermometer className="h-5 w-5 text-cyan-600" />
                    {rule.name}
                  </CardTitle>
                  <CardDescription>{rule.productCategory}</CardDescription>
                </div>
                <Switch
                checked={rule.enabled}
                onCheckedChange={() => toggleRule(rule.id)} />
              
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Temperature Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200">
                  <p className="text-xs text-muted-foreground">{t("minTemperature", "Min Temperature")}</p>
                  <p className="text-xl font-bold font-mono text-blue-600">{rule.tempMin}{t("c", "\xB0C")}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200">
                  <p className="text-xs text-muted-foreground">{t("maxTemperature", "Max Temperature")}</p>
                  <p className="text-xl font-bold font-mono text-red-600">{rule.tempMax}{t("c", "\xB0C")}</p>
                </div>
              </div>

              {/* Humidity Range (if applicable) */}
              {rule.humidityMin !== null &&
            <div className="grid grid-cols-2 gap-4">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">{t("minHumidity", "Min Humidity")}</p>
                    <p className="font-medium">{rule.humidityMin}%</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">{t("maxHumidity", "Max Humidity")}</p>
                    <p className="font-medium">{rule.humidityMax}%</p>
                  </div>
                </div>
            }

              {/* Alert Thresholds */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200">
                  <p className="text-xs text-amber-600">{t("alertAfter", "Alert After")}</p>
                  <p className="font-medium">{rule.alertThreshold}{t("min", "min")}</p>
                </div>
                <div className="p-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200">
                  <p className="text-xs text-red-600">{t("criticalAfter", "Critical After")}</p>
                  <p className="font-medium">{rule.criticalThreshold}{t("min", "min")}</p>
                </div>
              </div>

              {/* Auto Actions */}
              <div>
                <p className="text-sm font-medium mb-2">{t("automatedActions", "Automated Actions")}</p>
                <div className="flex flex-wrap gap-2">
                  {rule.autoActions.map((action) => {
                  const Icon = actionIcons[action] || Bell;
                  const actionInfo = autoActions.find((a) => a.id === action);
                  return (
                    <Badge key={action} variant="secondary" className="gap-1">
                        <Icon className="h-3 w-3" />
                        {actionInfo?.label}
                      </Badge>);

                })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-border">
                <Button variant="outline" size="sm" className="flex-1 gap-1">
                  <Edit className="h-3 w-3" />{t("edit", "Edit")}

              </Button>
                <Button variant="outline" size="sm" className="gap-1 text-red-600 hover:text-red-700">
                  <Trash2 className="h-3 w-3" />{t("delete", "Delete")}

              </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Auto Actions Legend */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle>{t("availableAutomatedActions", "Available Automated Actions")}</CardTitle>
          <CardDescription>{t("theseActionsCan", "These actions can be triggered automatically when thresholds are breached")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {autoActions.map((action) => {
              const Icon = actionIcons[action.id] || Bell;
              return (
                <div key={action.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="p-2 rounded-lg bg-cyan-100">
                    <Icon className="h-4 w-4 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{action.label}</p>
                    <p className="text-xs text-muted-foreground">{action.id}</p>
                  </div>
                </div>);

            })}
          </div>
        </CardContent>
      </Card>
    </div>);

};